import type { Network } from '../data/types';
import { edgeTo3D, type Vec3 } from './paths';

interface Seg {
  ax: number;
  ay: number;
  az: number;
  bx: number;
  by: number;
  bz: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * Spatial index to estimate a rendered elevation for arbitrary 2D points
 * (used to lift route-pattern polylines onto the track heights).
 */
export class ElevationIndex {
  private cell = 200;
  private grid = new Map<string, number[]>();
  private segs: Seg[] = [];

  constructor(network: Network, nodeHeights: Map<string, number>) {
    for (const edge of network.edges) {
      const pts: Vec3[] = edgeTo3D(edge, nodeHeights);
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const s: Seg = {
          ax: a.x, ay: a.y, az: a.z,
          bx: b.x, by: b.y, bz: b.z,
          minX: Math.min(a.x, b.x), maxX: Math.max(a.x, b.x),
          minZ: Math.min(a.z, b.z), maxZ: Math.max(a.z, b.z),
        };
        const idx = this.segs.length;
        this.segs.push(s);
        for (let gx = Math.floor(s.minX / this.cell); gx <= Math.floor(s.maxX / this.cell); gx++) {
          for (let gz = Math.floor(s.minZ / this.cell); gz <= Math.floor(s.maxZ / this.cell); gz++) {
            const key = `${gx},${gz}`;
            let arr = this.grid.get(key);
            if (!arr) { arr = []; this.grid.set(key, arr); }
            arr.push(idx);
          }
        }
      }
    }
  }

  heightAt(x: number, z: number): number {
    const gx = Math.floor(x / this.cell);
    const gz = Math.floor(z / this.cell);
    let best = 0;
    let bestD = Infinity;
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        const arr = this.grid.get(`${gx + dx},${gz + dz}`);
        if (!arr) continue;
        for (const i of arr) {
          const s = this.segs[i];
          const abx = s.bx - s.ax;
          const abz = s.bz - s.az;
          const len2 = abx * abx + abz * abz;
          let t = len2 > 0 ? ((x - s.ax) * abx + (z - s.az) * abz) / len2 : 0;
          t = Math.max(0, Math.min(1, t));
          const px = s.ax + t * abx;
          const pz = s.az + t * abz;
          const d = (x - px) * (x - px) + (z - pz) * (z - pz);
          if (d < bestD) {
            bestD = d;
            best = s.ay + (s.by - s.ay) * t;
          }
        }
      }
    }
    return best;
  }

  lift(coords: [number, number][]): { x: number; y: number; z: number }[] {
    return coords.map(([x, z]) => ({ x, y: this.heightAt(x, z), z }));
  }
}
